// Quote PDF generator — ported from v1 (ai-quote/index.html lines 19331–20347,
// see spec/14-module-crm.md Phase 5.3). Renders a customer-facing quote PDF
// using jsPDF + jspdf-autotable.
//
// Design follows v2's v8 mockup palette (orange #DC4318 accents, navy #1F2937
// headers) layered on a clean letter-size layout that should feel familiar to
// anyone reading v1's quote PDFs.
//
// jsPDF and jspdf-autotable are heavy (~200KB combined). We dynamic-import
// them inside generateQuotePdf so the main app bundle stays lean — the libs
// only load the first time the user actually exports a quote.
//
// No network calls happen at render time: the function is fully offline-safe.
// (The optional logoUrl is consumed via doc.addImage which fetches it eagerly,
// so if a remote logo URL is passed and the user is offline the logo is
// skipped — text header still renders. Pass a base64 data URL to guarantee
// rendering offline.)

import type { Quote, Customer, Part } from './crmTypes';

// =====================================================
// Public API
// =====================================================

export interface QuotePdfOptions {
  /** Letterhead company name. Default 'Keystone Machine & Fab'. */
  companyName?: string;
  /** Multi-line address — use '\n' between lines. */
  companyAddress?: string;
  companyPhone?: string;
  companyEmail?: string;
  /** Optional logo. Data URL ('data:image/png;base64,...') is preferred for
   * offline reliability; remote URLs work but require connectivity at render
   * time and silently skip the logo on failure. */
  companyLogoUrl?: string;
  /** Render the quote.notes block if present. Default true. */
  includeNotes?: boolean;
  /** Render the terms & conditions footer. Default true. */
  includeTermsAndConditions?: boolean;
  /** Override default validity language. Default '60 days'. */
  validityText?: string;
  /** Override the default terms paragraph. */
  termsText?: string;
}

/**
 * Generate a customer-facing quote PDF.
 *
 * The returned Blob is `application/pdf` — callers typically do:
 *
 *   const blob = await generateQuotePdf(quote, customer, parts);
 *   const url = URL.createObjectURL(blob);
 *   // download:  link.href = url; link.download = `${quote.number}.pdf`;
 *   // preview:   iframe.src = url;
 *   // remember:  URL.revokeObjectURL(url) when done.
 *
 * Pricing comes from `quote.partQuotedSpecs[partId].pricePerTier` — those are
 * the locked-in per-tier prices the rep approved when sending the quote.
 * Falls back to Part.materialCost.perPiece if a part has no quoted-spec entry
 * (e.g. an in-progress draft).
 */
export async function generateQuotePdf(
  quote: Quote,
  customer: Customer,
  parts: Part[],
  options?: QuotePdfOptions,
): Promise<Blob> {
  // Lazy-load the PDF libs so the main bundle stays small.
  const { jsPDF } = await import('jspdf');
  const autoTableModule = await import('jspdf-autotable');
  const autoTable = autoTableModule.default ?? autoTableModule.autoTable;

  const opts = withDefaults(options);
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });

  const PW = doc.internal.pageSize.getWidth(); // 612pt
  const PH = doc.internal.pageSize.getHeight(); // 792pt
  const M = 36; // margin (matches v1)

  // ============ HEADER ============
  let y = M;

  // Logo (left). Wrapped in try/catch — a bad URL must not break the PDF.
  if (opts.companyLogoUrl) {
    try {
      const props = doc.getImageProperties(opts.companyLogoUrl);
      const maxW = 140;
      const maxH = 60;
      const ratio = Math.min(maxW / props.width, maxH / props.height);
      const w = props.width * ratio;
      const h = props.height * ratio;
      const fmt = (props.fileType || 'PNG').toUpperCase();
      doc.addImage(
        opts.companyLogoUrl,
        fmt === 'JPG' ? 'JPEG' : fmt,
        M,
        y,
        w,
        h,
        undefined,
        'FAST',
      );
    } catch {
      // Logo failed — text letterhead still renders below.
    }
  }

  // QUOTATION title (right)
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  setTextColor(doc, COLOR_NAVY);
  doc.text('QUOTATION', PW - M, y + 22, { align: 'right' });
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  setTextColor(doc, COLOR_STEEL);
  const infoY = y + 38;
  doc.text(`Quote #: ${quote.number || '—'}`, PW - M, infoY, { align: 'right' });
  doc.text(
    `Date: ${formatDate(quote.createdAt ?? new Date().toISOString())}`,
    PW - M,
    infoY + 12,
    { align: 'right' },
  );
  doc.text(`Valid: ${opts.validityText}`, PW - M, infoY + 24, { align: 'right' });
  if (quote.rfqRef) {
    doc.text(`RFQ Ref: ${quote.rfqRef}`, PW - M, infoY + 36, { align: 'right' });
  }

  y += 70;

  // Shop info (left, under logo)
  doc.setFontSize(9);
  setTextColor(doc, COLOR_NAVY);
  doc.setFont('helvetica', 'bold');
  doc.text(opts.companyName, M, y);
  doc.setFont('helvetica', 'normal');
  setTextColor(doc, COLOR_STEEL);
  y += 12;
  if (opts.companyAddress) {
    for (const line of opts.companyAddress.split('\n')) {
      doc.text(line, M, y);
      y += 11;
    }
  }
  if (opts.companyPhone) {
    doc.text(opts.companyPhone, M, y);
    y += 11;
  }
  if (opts.companyEmail) {
    doc.text(opts.companyEmail, M, y);
    y += 11;
  }

  y = Math.max(y, M + 130);

  // Horizontal rule — industrial orange (v2 brand accent)
  setDrawColor(doc, COLOR_ORANGE);
  doc.setLineWidth(1.5);
  doc.line(M, y, PW - M, y);
  y += 16;

  // ============ BILL-TO / SHIP-TO ============
  // v2's Customer has billingAddress + optional shippingAddress. If shipping
  // is absent we treat it as same-as-billing (per v1 behavior).
  const billing = customer.billingAddress;
  const shipping = customer.shippingAddress;
  const shipSameAsBill = !shipping;
  const colW = (PW - 2 * M - 16) / 2;

  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  setTextColor(doc, COLOR_MUTE);
  doc.text('BILL TO', M, y);
  if (!shipSameAsBill) doc.text('SHIP TO', M + colW + 16, y);
  y += 12;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  setTextColor(doc, COLOR_NAVY);
  doc.text(customer.companyName, M, y);
  if (!shipSameAsBill) doc.text(customer.companyName, M + colW + 16, y);
  y += 12;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  setTextColor(doc, COLOR_STEEL);

  // Primary contact line — pull the first contact marked primary, or fall
  // back to the first contact entry.
  const primaryContact =
    customer.contacts.find((c) => c.primary) ?? customer.contacts[0];
  if (primaryContact?.name) {
    doc.text(primaryContact.name, M, y);
    y += 11;
  }
  if (primaryContact?.email) {
    doc.text(primaryContact.email, M, y);
    y += 11;
  }

  const billLines = formatAddressLines(billing);
  const shipLines = shipSameAsBill ? billLines : formatAddressLines(shipping);
  const maxLines = Math.max(billLines.length, shipLines.length);
  let blockY = y;
  for (let i = 0; i < maxLines; i++) {
    if (billLines[i]) doc.text(billLines[i], M, blockY);
    if (!shipSameAsBill && shipLines[i]) {
      doc.text(shipLines[i], M + colW + 16, blockY);
    }
    blockY += 11;
  }
  y = blockY + 8;

  // Sales rep / payment terms / job type strip
  const stripParts: string[] = [];
  if (customer.rep) stripParts.push(`Sales Rep: ${customer.rep}`);
  if (customer.terms) stripParts.push(`Payment Terms: ${customer.terms}`);
  if (quote.jobType) stripParts.push(`Job Type: ${humanJobType(quote.jobType)}`);
  if (quote.dueDate) stripParts.push(`Due Date: ${formatDate(quote.dueDate)}`);
  if (stripParts.length > 0) {
    doc.setFontSize(9);
    setTextColor(doc, COLOR_MUTE);
    doc.text(stripParts.join('   ·   '), M, y);
    y += 16;
  }

  y += 6;

  // ============ PARTS TABLE ============
  // Build the unified tier set across all quoted parts (matches v1).
  // Each part's quoted spec OWNS its tier list — falls back to part.qtyTiers.
  const allTiers = new Set<number>();
  for (const partId of quote.partIds ?? []) {
    const spec = quote.partQuotedSpecs?.[partId];
    const part = parts.find((p) => p.id === partId);
    const tiers = spec?.qtyTiers ?? part?.qtyTiers ?? quote.qtyTiers ?? [];
    for (const t of tiers) allTiers.add(t);
  }
  const tiers = Array.from(allTiers).sort((a, b) => a - b);

  // Order parts by quote.partIds (so the order matches the quote builder UI),
  // skipping any IDs we can't resolve.
  const orderedParts: Part[] = [];
  for (const partId of quote.partIds ?? []) {
    const p = parts.find((x) => x.id === partId);
    if (p) orderedParts.push(p);
  }

  const head: string[][] = [
    ['Part #', 'Description', 'Material'].concat(tiers.map((t) => `Qty ${t}`)),
  ];
  const body: string[][] = orderedParts.map((p) => {
    const spec = quote.partQuotedSpecs?.[p.id];
    const row: string[] = [p.number || '—', p.description || '', materialLabel(p)];
    for (const t of tiers) {
      const localTiers = spec?.qtyTiers ?? p.qtyTiers ?? [];
      const idx = localTiers.indexOf(t);
      if (idx === -1) {
        row.push('—');
        continue;
      }
      const price = pricePerPiece(spec, p, idx);
      row.push(price != null ? formatMoney(price) : '—');
    }
    return row;
  });

  const tierColumnStyles: Record<number, { halign: 'right'; cellWidth: number }> =
    {};
  tiers.forEach((_t, i) => {
    tierColumnStyles[3 + i] = { halign: 'right', cellWidth: 50 };
  });

  autoTable(doc, {
    startY: y,
    head,
    body,
    theme: 'grid',
    headStyles: {
      fillColor: COLOR_NAVY,
      textColor: [255, 255, 255],
      fontSize: 9,
      fontStyle: 'bold',
    },
    bodyStyles: {
      fontSize: 9,
      textColor: COLOR_NAVY,
    },
    alternateRowStyles: {
      fillColor: [249, 250, 251],
    },
    columnStyles: {
      0: { cellWidth: 72, fontStyle: 'bold' },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 70 },
      ...tierColumnStyles,
    },
    margin: { left: M, right: M },
  });

  y = autoTableFinalY(doc, y) + 14;

  // ============ QUOTE TOTAL (per-tier subtotals) ============
  if (tiers.length > 0 && orderedParts.length > 0) {
    if (y > PH - 200) {
      doc.addPage();
      y = M;
    }

    const tierTotals = tiers.map((t) => {
      let subtotal = 0;
      for (const p of orderedParts) {
        const spec = quote.partQuotedSpecs?.[p.id];
        const localTiers = spec?.qtyTiers ?? p.qtyTiers ?? [];
        const idx = localTiers.indexOf(t);
        if (idx === -1) continue;
        const unit = pricePerPiece(spec, p, idx);
        if (unit == null) continue;
        subtotal += unit * t;
      }
      return { qty: t, total: subtotal };
    });

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    setTextColor(doc, COLOR_NAVY);
    doc.text('Quote Total', M, y);
    y += 4;

    const totalHead: string[][] = [
      ['Quantity'].concat(tierTotals.map((d) => `Qty ${formatInt(d.qty)}`)),
    ];
    const totalBody: string[][] = [
      ['Parts subtotal'].concat(tierTotals.map((d) => formatMoney(d.total))),
    ];
    const totalFoot: string[][] = [
      ['TOTAL'].concat(tierTotals.map((d) => formatMoney(d.total))),
    ];

    const totalTierColumnStyles: Record<
      number,
      { halign: 'right'; cellWidth: number }
    > = {};
    tierTotals.forEach((_d, i) => {
      totalTierColumnStyles[i + 1] = { halign: 'right', cellWidth: 80 };
    });

    autoTable(doc, {
      startY: y + 4,
      head: totalHead,
      body: totalBody,
      foot: totalFoot,
      theme: 'grid',
      headStyles: {
        fillColor: COLOR_NAVY,
        textColor: [255, 255, 255],
        fontSize: 9,
        fontStyle: 'bold',
        halign: 'right',
      },
      bodyStyles: {
        fontSize: 9,
        textColor: COLOR_NAVY,
        halign: 'right',
      },
      footStyles: {
        fillColor: COLOR_ORANGE,
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 11,
        halign: 'right',
      },
      columnStyles: {
        0: { cellWidth: 'auto', halign: 'left', fontStyle: 'bold' },
        ...totalTierColumnStyles,
      },
      margin: { left: M, right: M },
    });
    y = autoTableFinalY(doc, y) + 14;
  }

  // ============ NOTES ============
  if (opts.includeNotes) {
    const noteLines = collectNotes(quote);
    if (noteLines.length > 0) {
      if (y > PH - 180) {
        doc.addPage();
        y = M;
      }
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      setTextColor(doc, COLOR_MUTE);
      doc.text('NOTES', M, y);
      y += 12;
      doc.setFont('helvetica', 'normal');
      setTextColor(doc, COLOR_NAVY);
      const wrapped = doc.splitTextToSize(noteLines.join('\n'), PW - 2 * M);
      doc.text(wrapped, M, y);
      y += wrapped.length * 11 + 8;
    }
  }

  // ============ FOOTER (terms) ============
  if (opts.includeTermsAndConditions) {
    const footerLines = doc.splitTextToSize(opts.termsText, PW - 2 * M);
    const footerH = footerLines.length * 9 + 30;
    let footerY = PH - M - footerH;
    if (footerY < y + 10) {
      doc.addPage();
      footerY = PH - M - footerH;
    }
    setDrawColor(doc, COLOR_ORANGE);
    doc.setLineWidth(0.5);
    doc.line(M, footerY, PW - M, footerY);
    footerY += 12;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    setTextColor(doc, COLOR_MUTE);
    doc.text('TERMS & CONDITIONS', M, footerY);
    footerY += 10;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    setTextColor(doc, COLOR_STEEL);
    doc.text(footerLines, M, footerY);
  }

  // ============ Footer signature line (every page) ============
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    setTextColor(doc, COLOR_MUTE);
    doc.text(
      `Generated ${new Date().toLocaleDateString()} · ${opts.companyName}`,
      M,
      PH - 18,
    );
    doc.text(`Page ${i} of ${totalPages}`, PW - M, PH - 18, { align: 'right' });
  }

  return doc.output('blob');
}

// =====================================================
// Internals
// =====================================================

// RGB tuples — jsPDF wants integers 0-255 for setTextColor / setDrawColor /
// autoTable fillColor. Mirrors tailwind.config.ts brand palette.
const COLOR_ORANGE: [number, number, number] = [220, 67, 24]; // #DC4318
const COLOR_NAVY: [number, number, number] = [31, 41, 55]; // #1F2937
const COLOR_STEEL: [number, number, number] = [100, 116, 139]; // #64748B
const COLOR_MUTE: [number, number, number] = [107, 114, 128]; // mid-gray for labels

interface ResolvedOptions {
  companyName: string;
  companyAddress?: string;
  companyPhone?: string;
  companyEmail?: string;
  companyLogoUrl?: string;
  includeNotes: boolean;
  includeTermsAndConditions: boolean;
  validityText: string;
  termsText: string;
}

function withDefaults(o?: QuotePdfOptions): ResolvedOptions {
  return {
    companyName: o?.companyName ?? 'Keystone Machine & Fab',
    companyAddress: o?.companyAddress,
    companyPhone: o?.companyPhone,
    companyEmail: o?.companyEmail,
    companyLogoUrl: o?.companyLogoUrl,
    includeNotes: o?.includeNotes ?? true,
    includeTermsAndConditions: o?.includeTermsAndConditions ?? true,
    validityText: o?.validityText ?? '60 days',
    termsText:
      o?.termsText ??
      [
        'Quote valid for 60 days from date above. Prices in USD.',
        'Tooling, freight, and applicable taxes additional unless explicitly included.',
        'Standard lead time begins upon receipt of signed PO and any required deposit.',
        'KMF reserves the right to re-quote if material costs change materially before order acceptance.',
      ].join(' '),
  };
}

function setTextColor(
  doc: { setTextColor: (r: number, g: number, b: number) => void },
  rgb: [number, number, number],
): void {
  doc.setTextColor(rgb[0], rgb[1], rgb[2]);
}

function setDrawColor(
  doc: { setDrawColor: (r: number, g: number, b: number) => void },
  rgb: [number, number, number],
): void {
  doc.setDrawColor(rgb[0], rgb[1], rgb[2]);
}

/**
 * autoTable in v5 doesn't define `lastAutoTable` on the public jsPDF type,
 * but it does attach one at runtime, and exposes a typed `getLastAutoTable()`.
 * Use that and fall back to the original cursor y if for some reason it's null
 * (e.g. table content was empty).
 */
function autoTableFinalY(doc: unknown, fallback: number): number {
  const d = doc as { lastAutoTable?: { finalY?: number } };
  return d.lastAutoTable?.finalY ?? fallback;
}

/** ISO date / datetime → 'MMM D, YYYY'. Defensive for partial inputs. */
function formatDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatMoney(n: number): string {
  return (
    '$' +
    n.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
}

function formatInt(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return Math.round(n).toLocaleString('en-US');
}

function humanJobType(jobType: string): string {
  // Convert 'multi_part_production' → 'Multi part production'
  return jobType
    .split('_')
    .map((w, i) => (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');
}

function formatAddressLines(addr?: {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  zip: string;
  country?: string;
}): string[] {
  if (!addr) return [];
  const lines: string[] = [];
  if (addr.line1) lines.push(addr.line1);
  if (addr.line2) lines.push(addr.line2);
  const cityLine = [addr.city, addr.state && `, ${addr.state}`, ` ${addr.zip ?? ''}`]
    .filter(Boolean)
    .join('')
    .trim();
  if (cityLine) lines.push(cityLine);
  if (addr.country && addr.country !== 'US' && addr.country !== 'USA') {
    lines.push(addr.country);
  }
  return lines;
}

function materialLabel(part: Part): string {
  // v2's Part doesn't have a free-text material field on the entity itself —
  // that's stored in materialCost or in attachedDrawings notes. Until M5
  // surfaces a proper material string, show vendorPartNum as the closest
  // reader-facing identifier; otherwise blank.
  const mc = part.materialCost;
  if (mc?.vendorPartNum) return mc.vendorPartNum;
  return '';
}

/**
 * Resolve unit price for a (part, tier-index) pair. Prefers the quote's
 * frozen pricePerTier snapshot; falls back to the canonical Part's
 * materialCost.perPiece array if no snapshot exists.
 */
function pricePerPiece(
  spec: { pricePerTier?: number[] } | undefined,
  part: Part,
  idx: number,
): number | null {
  const fromSpec = spec?.pricePerTier?.[idx];
  if (typeof fromSpec === 'number' && Number.isFinite(fromSpec)) return fromSpec;
  const fromPart = part.materialCost?.perPiece?.[idx];
  if (typeof fromPart === 'number' && Number.isFinite(fromPart)) return fromPart;
  return null;
}

/**
 * Gather customer-facing notes. Today we only surface quote-level notes via
 * the parts list (v2's Quote doesn't define a top-level notes string yet) and
 * any per-part `notes` from each Part record. Per-part notes are prefixed
 * with the part number so the reader knows what they're about.
 */
function collectNotes(_quote: Quote): string[] {
  // Reserved for future quote.notes; v2 Quote shape (crmTypes.ts) doesn't
  // expose a top-level `notes` field yet, so for now this is empty.
  // Per-part notes intentionally are NOT pulled into the customer PDF —
  // those are usually internal shop notes (setup tips, material caveats).
  // Once Module 5 adds a customer-visible quote-level notes field this
  // function is the one place that needs to surface it.
  return [];
}
